import { OrderDeliveryKind, OrderDeliveryState } from '@codezo/graphql';
import type { OrderDeliveryRow, Tone } from '@codezo/order-detail';
import { ExternalLink } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { formatDate } from '../../../lib/utils';

/** The minimum a delivery has to look like. Structural, so both apps pass theirs. */
export interface DeliveryInput {
	id: string;
	kind: OrderDeliveryKind;
	/**
	 * False only for a virtual sample: it arrives as files, so there is no
	 * carrier, no tracking and no dispatch. One field, rather than this file
	 * knowing what VIRTUAL_SAMPLE implies — the reason the row used to read as a
	 * parcel is that nothing told it not to.
	 */
	isPhysical: boolean;
	reference: string;
	state: OrderDeliveryState;
	stateLabel: string;
	carrier?: string | null;
	trackingNumber?: string | null;
	/**
	 * Resolved server-side from the shared carrier list. Null on a virtual sample
	 * and null when the carrier is unrecognised or has no tracking page — so a
	 * "Track" control renders only when there is somewhere real to go.
	 *
	 * Never built here from the carrier name: that column is free text predating
	 * the picker, so rows hold whatever was typed, and a guessed link that 404s is
	 * worse than the plain number.
	 */
	trackingUrl?: string | null;
	shippingMethodLabel?: string | null;
	projected: boolean;
	expectedEarliest?: string | null;
	expectedLatest?: string | null;
	shippedAt?: string | null;
	actualDeliveryDate?: string | null;
	lines: { orderItemId: string; productName?: string | null; quantity: number }[];
}

/**
 * Display tones for the delivery states. An enum, so exhaustive — a new state
 * breaks the build rather than rendering grey.
 */
const STATE_TONE: Record<OrderDeliveryState, Tone> = {
	[OrderDeliveryState.Delivered]: 'success',
	[OrderDeliveryState.InTransit]: 'info',
	[OrderDeliveryState.Ready]: 'info',
	[OrderDeliveryState.InProduction]: 'warn',
	[OrderDeliveryState.Awaiting]: 'neutral',
	[OrderDeliveryState.Cancelled]: 'neutral',
};

/**
 * A window, with the year said once.
 *
 * `formatDate` carries the year, which is right for a single date and wasteful
 * twice in a row: "Nov 3, 2026 – Nov 9, 2026" is half repetition, and in a 320px
 * rail it wraps under the state pill. Same year → the opening date drops it.
 * Across a year boundary both keep it, because there the year is the point.
 */
function dateRange(from: string, to: string): string {
	const start = new Date(from);
	const end = new Date(to);
	if (start.getFullYear() !== end.getFullYear()) {
		return `${formatDate(from)} – ${formatDate(to)}`;
	}
	const opening = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
	return `${opening} – ${formatDate(to)}`;
}

/**
 * When, as a label and a value.
 *
 * The label is the VERB, because that is the part a reader needs in order to
 * know what the date means: a box ARRIVES, a virtual sample is SUBMITTED, and
 * anything not yet here is EXPECTED. It used to be folded into the value as one
 * string, which left a bare "Sep 17, 2026" sitting under a title with nothing
 * saying whether it had happened.
 *
 * `actualDeliveryDate` is the only date that means it is here. Everything else
 * is a window, and while `projected` it is production plus transit, counted from
 * when production started — or from today, before it has. So it slides out each
 * day nothing has begun, or once production runs over. That is honest only if it
 * has never been shown as a promise, which is why a projection keeps "Expected"
 * and is set quieter by the row.
 *
 * A digital row never says "Arriving". A file is not in transit, and that is the
 * exact word that made one read as a parcel.
 *
 * Null is a real answer: no lead time was ever agreed, the same no-window answer
 * the quote gives.
 */
function when(d: DeliveryInput): { label: string; value: string } | undefined {
	if (d.actualDeliveryDate) {
		return {
			label: d.isPhysical ? 'Arrived' : 'Submitted',
			value: formatDate(d.actualDeliveryDate),
		};
	}
	if (!d.expectedEarliest && !d.expectedLatest) return undefined;

	const value =
		d.expectedEarliest && d.expectedLatest
			? d.expectedEarliest === d.expectedLatest
				? formatDate(d.expectedEarliest)
				: dateRange(d.expectedEarliest, d.expectedLatest)
			: d.expectedEarliest
				? formatDate(d.expectedEarliest)
				: formatDate(d.expectedLatest!);

	return { label: d.projected || !d.isPhysical ? 'Expected' : 'Arriving', value };
}

/**
 * What a shipment is carrying.
 *
 * One product in the box: just the quantity. The row's title already names it,
 * and "Keychain × 100" under a heading reading "Keychain" is the line that made
 * this block read as noise — it spent a whole row repeating a word.
 *
 * Several products: each one named, because there the reference names none of
 * them and the quantities differ.
 *
 * Samples carry nothing here. The reference already says "Keychain sample", and
 * a sample is one of one, so there is neither a name to add nor a quantity.
 */
function contents(d: DeliveryInput): string | null {
	if (d.kind !== OrderDeliveryKind.Shipment || d.lines.length === 0) return null;
	if (d.lines.length === 1) {
		const units = d.lines[0].quantity;
		return `${units.toLocaleString()} ${units === 1 ? 'unit' : 'units'}`;
	}
	return d.lines
		.map((l) => `${l.productName ?? 'Product'} × ${l.quantity.toLocaleString()}`)
		.join(' · ');
}

/**
 * Turn the server's deliveries into rows, grouped.
 *
 * Kept app-side rather than in `@codezo/order-detail` on purpose: the package
 * takes no schema dependency, so the enums stop here and it receives words and
 * tones. That is the same split `money.ts` makes, and it is why this file has a
 * twin in the organization app. The two are currently identical, and should stay
 * that way unless something genuinely internal has to appear here: a delivery is
 * a shared fact, and the studio and the client disagreeing about where a box is
 * has never been the useful kind of difference.
 *
 * ── Samples, then shipments ──
 *
 * The server may hand these over interleaved by product — sample, goods, sample,
 * goods — which reads as four rows of the same thing told apart by one word
 * inside a title. They are not the same thing: a sample is one unit going out
 * for approval, a shipment is the order. The rest of the page already keeps
 * those apart in the summary, the payment buckets and the payment routes, so
 * they are kept apart here too, in that order — sampling happens first, and a
 * reader looking for "where are my goods" is looking for the later block.
 *
 * Order WITHIN each block is the server's and is left alone: shipments arrive by
 * sequence, and a rejected round sits before its replacement.
 *
 * Cancelled rows are kept and greyed rather than dropped — the backend hands
 * over the whole list deliberately, and a round that was made and rejected is
 * part of what happened to this order.
 */
function tracking(d: DeliveryInput): OrderDeliveryRow['tracking'] {
	// No tracking affordance on a digital row, whatever else is set: nothing
	// travels, and a tracking line is half of what made one read as a parcel.
	if (!d.isPhysical || !d.trackingNumber) return undefined;

	return {
		label: (
			<>
				{d.carrier ? `${d.carrier} · ` : ''}
				<span className="font-mono tabular-nums">{d.trackingNumber}</span>
			</>
		),
		// Only where the server resolved somewhere real to go. Never built from
		// the carrier name: that column is free text predating the picker, so a
		// row holds whatever was typed and a guessed link that 404s is worse than
		// the plain number.
		action: d.trackingUrl ? (
			<Button
				size="xs"
				variant="outline"
				onClick={() => window.open(d.trackingUrl!, '_blank', 'noopener')}
			>
				Track
				<ExternalLink size={12} data-icon="inline-end" />
			</Button>
		) : undefined,
	};
}

/**
 * The controls for one row, if the caller has any for it.
 *
 * A delivery of kind SHIPMENT and the shipment it projects share an ID — that is
 * the join, and it is the whole reason this list can carry actions at all. Rows
 * of kind SAMPLE or VIRTUAL_SAMPLE have no shipment behind them and get nothing
 * from here; a sample's moves live on `OrderItemSample.availableActions`, which
 * is a different record with a different set.
 */
export type DeliveryActionRenderer = (shipmentId: string) => OrderDeliveryRow['actions'];

export function toDeliveryRows(
	deliveries: DeliveryInput[],
	/**
	 * Omitted by the client app on purpose. Every shipment action is the studio's,
	 * and the mutation behind them is admin/vetter — so the client's copy of this
	 * list stays a read-only account of where things are.
	 */
	shipmentActions?: DeliveryActionRenderer
): OrderDeliveryRow[] {
	const isSample = (d: DeliveryInput) => d.kind !== OrderDeliveryKind.Shipment;
	const ordered = [...deliveries.filter(isSample), ...deliveries.filter((d) => !isSample(d))];

	return ordered.map((d) => ({
		key: d.id,
		// A heading is drawn where this changes. "Products" rather than
		// "Shipments" to match the words the rest of the page already uses for the
		// same split — Products subtotal, Product delivery, Production payment —
		// so a reader meets one vocabulary, not two names for one half of an order.
		//
		// The rows under it are still shipments, and one box can carry several
		// products; the heading names the CATEGORY, and each row says what is in it.
		group: isSample(d) ? 'Samples' : 'Products',
		reference: d.reference,
		state: { label: d.stateLabel, tone: STATE_TONE[d.state] },
		contents: contents(d),
		when: when(d),
		// Only tone now — the caveat itself lives in the verb `when` chose. A
		// forecast is set quieter than a fact.
		projected: d.projected,
		// Server-worded, so "Express" stays whatever the studio calls it. Absent
		// on a virtual sample, which does not travel at all.
		method: d.shippingMethodLabel,
		tracking: tracking(d),
		// Only a shipment row has a record behind it that owns actions, and the
		// delivery's id IS that shipment's id.
		actions: isSample(d) ? undefined : shipmentActions?.(d.id),
		cancelled: d.state === OrderDeliveryState.Cancelled,
	}));
}
